import "./SearchForm.scss";
import useDirection from "hooks/useDirection";
import "styles/yeloplus-booking-type.scss";
import "styles/yeloplus-select-class.scss";
import "styles/yeloplus-select-class-extras.scss";
import "styles/yeloplus-class-detail.scss";
import "styles/yeloplus-booking-flow-rtl.scss";
import "styles/yeloplus-vehicle-mobile.scss";
import Checkout from "./Checkout";
import FormHeader from "./FormHeader";
import { Container } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import ServiceClassTabs from "./ServiceClassTabs";
import AdditionalInfoForm from "./AdditionalInfoForm";
import BookingService from "services/booking.service";
import ToasterService from "utils/toaster.util";
import LocalStorage from "utils/localstorage.util";
import { classActions } from "redux/slices/class";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { change } from "redux-form";
import format from "date-fns/format";
import { bookingActions } from "redux/slices/booking";
import { store } from "redux/store";
import { canonicalTime } from "utils/timeFormat.util";
import { MODAL, modalActions } from "redux/slices/modal";
import {
  isPartnerWebviewSession,
  isYeloAppSession,
  partnerHomePath,
  PARTNER_CHECKOUT_STORAGE_KEY,
  withPartnerToken,
} from "utils/partnerSession.util";
import {
  clearAggregatorPromoFormFields,
  getAggregatorDisplayCurrency,
  getAggregatorPriceFlags,
  getAggregatorSourcePayload,
  stripAggregatorPromoFromBookingData,
} from "utils/aggregatorHourlyPricing.util";
import {
  getStoredPartnerUser,
  persistPartnerUser,
  resolvePartnerPassengerEmail,
} from "utils/partnerUser.util";
import {
  trackButtonBookTrip,
  trackPartnerBookingCreated,
  trackPartnerBookingStep,
  trackScreenTripBooking,
} from "services/mixpanel.service";
import { shouldEnableYeloAppAnalytics } from "utils/analytics.util";

/* How long after a booking step appears its main button ignores taps. */
const STEP_TAP_LOCK_MS = 400;

export default function SearchForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation("translation");
  const direction = useDirection();
  const dispatch = useAppDispatch();
  const yeloApp = isYeloAppSession();
  const reviewPage = 3;
  const hashTags: Record<number, string> = {
    1: "classes",
    2: "additional-form",
    3: "checkout",
  };

  const resolvePageFromHash = (hash: string) => {
    const pages: Record<string, number> = {
      classes: 1,
      "additional-form": 2,
      checkout: 3,
    };
    return pages[hash] || 1;
  };

  const [page, setPage] = useState(1);
  const bookingFormPayload = LocalStorage.getItem("BookingFormPayload");
  const AdditionalFormPayload = LocalStorage.getItem("AdditionalInfoForm");
  const classsPrice = useAppSelector((state) => state.classes.classsPrice);
  const classesPrices = useAppSelector((state) => state.classes.classesPrices);

  useEffect(() => {
    LocalStorage.setFormValues("SearchForm", dispatch);
    LocalStorage.setFormValues("AdditionalInfoForm", dispatch);
    LocalStorage.setFormValues("PickUpForm", dispatch);

    const storedUser = getStoredPartnerUser();
    if (storedUser) {
      persistPartnerUser(storedUser, dispatch);
    }
  }, [dispatch]);

  /* The step follows the URL hash. Steps move with router navigate(), which
     uses pushState — that never fires `hashchange`, so a listener missed every
     in-app jump (e.g. "Edit" on the review screen changed the hash but left
     the review step on screen). The router location covers both in-app
     navigation and the hardware/browser back button. */
  useEffect(() => {
    setPage(resolvePageFromHash(location.hash.replace("#", "")));
  }, [location.hash]);

  useEffect(() => {
    if (!shouldEnableYeloAppAnalytics()) return;
    trackScreenTripBooking();
  }, []);

  useEffect(() => {
    if (!shouldEnableYeloAppAnalytics()) return;
    const hash = location.hash.replace("#", "");
    const stepMap: Record<string, "classes" | "additional_form" | "checkout"> = {
      classes: "classes",
      "additional-form": "additional_form",
      checkout: "checkout",
    };
    const step = stepMap[hash];
    if (step) {
      trackPartnerBookingStep({ step });
    }
  }, [location.hash]);

  // Review step needs a selected class — send back to vehicle list if missing.
  useEffect(() => {
    if (page !== reviewPage || classsPrice?.price_details) return;
    if (!classesPrices?.length) return;
    setPage(1);
    navigate(withPartnerToken("/search-form#classes"), { replace: true });
  }, [page, reviewPage, classsPrice, classesPrices, navigate]);

  // useEffect(() => {
  //   StripLoaderService.getStripeInstance();
  // }, []);

  useEffect(() => {
    /* Set false once this run is superseded or the flow is left: a late
       answer must not send the reader home or open a modal over whatever
       screen they are on by then. */
    let active = true;
    const getBookingPrices = async () => {
      const data = LocalStorage.getItem("BookingFormPayload");

      if (!data) return navigate(isPartnerWebviewSession() ? partnerHomePath() : "/");

      /* PickUp normally fetched prices already. Persisted responses from an
         older gallery revision must be refreshed once; otherwise a device can
         keep an obsolete cabin image indefinitely. */
      const hasCurrentGalleryContract =
        classesPrices?.length > 0 &&
        classesPrices.every(
          (price: any) => price.seating_gallery_version === 5
        );
      if (hasCurrentGalleryContract) {
        return;
      }

      const applyPricesResult = (result: any, payload: any) => {
        const { classes, distance, isStripe, isHyperpay, aggregator, user } =
          result;

        if (!classes?.length) {
          ToasterService.showError(
            `No classes in ${payload.city_name?.[0] || data.city_name?.[0] || "selected area"}`
          );
          navigate(isPartnerWebviewSession() ? partnerHomePath() : "/");
          return;
        }

        const stored = LocalStorage.getItem("BookingFormPayload") || {};
        const nextPayload = {
          ...stored,
          ...payload,
          isStripe: !!isStripe,
          isHyperpay: !!isHyperpay,
          aggregator: !!aggregator,
          ...getAggregatorSourcePayload(result),
          distance,
        };
        persistPartnerUser(user, dispatch);
        LocalStorage.setItem("BookingFormPayload", nextPayload);
        dispatch(change("SearchTripForm", "distance", distance));
        if (aggregator) clearAggregatorPromoFormFields(dispatch);
        dispatch(classActions.setClassesPrices(classes));
      };

      const [success]: any = await BookingService.getBookingPrices(data);
      if (!active) return;

      if (success) {
        const result = success.data.data;
        const flags = getAggregatorPriceFlags(result);

        if (flags.needsDestination) {
          return dispatch(
            modalActions.openModal({
              type: MODAL.HOURLY_DESTINATION,
              width: "520px",
              data: {
                priceRequest: data,
                onSuccess: (modalResult: any, modalPayload: any) =>
                  applyPricesResult(modalResult, modalPayload),
              },
            })
          );
        }

        if (flags.adjustDuration) {
          ToasterService.showError(
            flags.message || t("hourly_increase_duration_message")
          );
          return navigate(isPartnerWebviewSession() ? partnerHomePath() : "/");
        }

        applyPricesResult(result, data);
        return;
      }

      navigate(isPartnerWebviewSession() ? partnerHomePath() : "/");
    };

    getBookingPrices();
    return () => {
      active = false;
    };
  }, [dispatch, navigate, classesPrices, t]);

  useEffect(
    () =>
      window.scrollTo({
        top: 0,
        behavior: "auto",
      }),
    [page]
  );

  /* Every step's main button sits in the same spot at the bottom of the
     screen, and the next step renders the moment the last one is left. A
     quick double or triple tap therefore landed on the NEXT step's button
     too: Continue → Continue → Book Now sent a booking the user never saw
     the passenger form or review for. Taps arriving within this window of a
     step change are dropped — far shorter than anyone takes to read a step
     and tap on purpose. Set on every step change, including the hardware
     back button, and synchronously in next/prev so two taps in one tick
     cannot both move. */
  const stepLockUntil = useRef(0);
  const isStepLocked = () => Date.now() < stepLockUntil.current;
  const lockStep = () => {
    stepLockUntil.current = Date.now() + STEP_TAP_LOCK_MS;
  };

  useEffect(() => {
    stepLockUntil.current = Date.now() + STEP_TAP_LOCK_MS;
  }, [page]);

  /* Held from the booking request until it settles. On success the screen
     is left (payment or booking details), so it stays
     held — a tap during that navigation must not book again. */
  const submittingBooking = useRef(false);

  const stepPath = (step: number) =>
    isPartnerWebviewSession()
      ? withPartnerToken(`/search-form#${hashTags[step]}`)
      : `/search-form#${hashTags[step]}`;

  /* Going back a step pops the history entry nextPage pushed, so the header
     back arrow and the Android back button walk the same stack instead of the
     arrow pushing a new entry that the hardware button then walks "forward"
     through. With no in-flow entry to pop (deep link, return from payment),
     the current entry is replaced. */
  const prevPage = () => {
    if (isStepLocked()) return;
    lockStep();
    const pageValue = page - 1;
    if ((location.state as any)?.fromStep === pageValue) {
      navigate(-1);
      return;
    }
    setPage(pageValue);
    navigate(stepPath(pageValue), { replace: true });
  };

  const nextPage = () => {
    if (isStepLocked()) return;
    lockStep();
    const pageValue = page + 1;
    setPage(pageValue);
    navigate(stepPath(pageValue), { state: { fromStep: page } });
  };

  const handleSubmit = async (values: any) => {
    if (isStepLocked() || submittingBooking.current) return;
    console.log(values);

    if (shouldEnableYeloAppAnalytics()) {
      trackButtonBookTrip({
        vehicle_class: classsPrice?.external_class_name || classsPrice?.class_name,
        price: classsPrice?.price_details?.brutto,
        trip_type: bookingFormPayload?.trip_type,
      });
    }

    const data = { ...values };
    const date = new Date(data.date.date).toDateString();
    const time = canonicalTime(data.time);

    data.added_from = "website";

    data.payment_type = "s";
    data.time_stamp = format(
      new Date(`${date} ${time}`),
      "yyyy-MM-dd HH:mm:ss"
    );
    const splitDate = data.time_stamp.split(" ");
    data.time_stamp = `${splitDate[0]}T${splitDate[1]}.000Z`;
    data.time_format = store.getState().booking.timeFormat ?? "24";
    data.booking_for_someone = true;

    const passengerForm = AdditionalFormPayload;
    const storedPartnerUser = getStoredPartnerUser();

    if (data.booking_for_someone && passengerForm) {
      const {
        phone,
        email,
        title,
        last_name,
        first_name,
      } = passengerForm;

      let passengerPhone = "";
      let passengerCountry = passengerForm.country;
      let passengerCountryCode = passengerForm.country_code;

      if (phone?.data) {
        passengerPhone = `+${phone.value}`;
        passengerCountry = phone.data.countryCode;
        passengerCountryCode = `+${phone.data.dialCode}`;
      } else if (phone?.value) {
        passengerPhone = `${phone.value}`.startsWith("+")
          ? `${phone.value}`
          : `+${phone.value}`;
      }

      data.booking_for_details = {
        phone: passengerPhone,
        email: resolvePartnerPassengerEmail(
          { email },
          storedPartnerUser
        ),
        title,
        country: passengerCountry,
        last_name,
        first_name,
        country_code: passengerCountryCode || "+966",
      };
    }

    data.trip_type = bookingFormPayload?.trip_type || "hourly";
    data.pickup_type = data?.pickup?.details?.airport ? "airport" : "place";

    data.city_name = Array.isArray(bookingFormPayload?.city_name)
      ? [...bookingFormPayload.city_name]
      : [data?.pickup?.details?.city];

    const { pickup, destination } = data;
    // const pickupSuggestion = {
    //   name: pickup?.details?.name,
    //   title: pickup?.details?.name,
    //   place_id: pickup?.details?.place_id,
    //   address: pickup?.details?.formatted_address,
    //   city: pickup?.details?.city,
    //   country_name_code: pickup?.details?.country_name_code,
    //   isPriorityCountry: pickup?.details?.isPriorityCountry || false,
    //   isRuhAirport: pickup?.details?.airport || false,
    //   coordinates: {
    //     lat: pickup?.details?.geometry?.location?.lat,
    //     lng: pickup?.details?.geometry?.location?.lng,
    //   },
    //   types: pickup?.details?.types || [],
    //   score: pickup?.score || 0,
    // };

    // let destinationSuggestion:any = null;
    // if (destination?.details) {
    //   destinationSuggestion = {
    //     name: destination?.details?.name,
    //     title: destination?.details?.name,
    //     place_id: destination?.details?.place_id,
    //     address: destination?.details?.formatted_address,
    //     city: destination?.details?.city,
    //     country_name_code: destination?.details?.country_name_code,
    //     isPriorityCountry: destination?.details?.isPriorityCountry || false,
    //     isRuhAirport: destination?.details?.airport || false,
    //     coordinates: {
    //       lat: destination?.details?.geometry?.location?.lat,
    //       lng: destination?.details?.geometry?.location?.lng,
    //     },
    //     types: destination?.details?.types || [],
    //     score: destination?.score || 0,
    //   };
    // }

    if (data?.pickup) {
      data.pickup = {
        title: pickup?.details?.name,
        place_id: pickup?.details?.place_id,
        address: pickup?.details?.formatted_address,
        coordinates: {
          lat: pickup?.details?.geometry?.location?.lat,
          lng: pickup?.details?.geometry?.location?.lng,
        },
      };
    }

    if (bookingFormPayload?.trip_type === "trip") {
      const locationDistination = data.destination.details?.geometry?.location;
      data.trip_type = "trip";
      data.city_name.push(data?.destination?.details?.city);
      data.destination = {
        title: destination?.details?.name,
        place_id: destination?.details?.place_id,
        address: destination?.details?.formatted_address,
        coordinates: {
          lat: destination?.details?.geometry?.location?.lat,
          lng: destination?.details?.geometry?.location?.lng,
        },
      };
      data.destinationCoordinates = [
        locationDistination.lat,
        locationDistination.lng,
      ];
      data.pickup_type =
        data.pickup_type === "airport"
          ? "airport"
          : destination?.details.airport
            ? "airport"
            : "place";

      delete data.duration;
    } else {
      data.duration = Number(
        bookingFormPayload?.duration ?? data.duration
      );
      if (
        bookingFormPayload?.aggregator &&
        bookingFormPayload?.destination_details
      ) {
        data.destination = bookingFormPayload.destination_details;
        if (bookingFormPayload.destinationCoordinates) {
          data.destinationCoordinates = bookingFormPayload.destinationCoordinates;
        }
        if (Array.isArray(bookingFormPayload.coordinates)) {
          data.coordinates = bookingFormPayload.coordinates;
        }
      } else {
        delete data.destination;
      }
    }
    const { location } = pickup?.details?.geometry;
    const isHourlyBooking = data.trip_type === "hourly";
    data.coordinates = [[location.lng, location.lat]];
    const destLocation = destination?.details?.geometry?.location;
    if (isHourlyBooking) {
      /* Hourly has no destination field: PickUpForm.destination is whatever an
         earlier transfer search left behind. Booking must resolve the same zone
         the quote did, so reuse the quote's coordinates (pickup, plus the
         drop-off from the hourly destination modal) — never the stale field. */
      if (Array.isArray(bookingFormPayload?.coordinates)) {
        data.coordinates = bookingFormPayload.coordinates;
      }
    } else if (destLocation) {
      data.coordinates.push([destLocation.lng, destLocation.lat]);
    }
    data.pickupCoordinates = [location.lat, location.lng];
    /* As in yelo-usernext: the flight number is the booking's top-level
       `flight_number` (what the add-trip DTO, the driver app and updates
       read), never part of additional_info, and it belongs to an airport
       pickup — the only case the passenger step asks for it. The passenger
       form is kept in localStorage between bookings, so a number typed for
       an earlier airport pickup must not ride along with a city booking. */
    const pickupIsAirport = Boolean(pickup?.details?.airport);
    const flightNumber = pickupIsAirport
      ? String(AdditionalFormPayload?.flight_number ?? "").trim()
      : "";
    data.additional_info = {
      notes: AdditionalFormPayload?.notes || values.notes,
      pickup_sign: AdditionalFormPayload?.pickup_sign || values.pickup_sign,
      child_seats: AdditionalFormPayload?.child_seats,
    };
    if (flightNumber) data.flight_number = flightNumber;
    data.business_partner_details = {
      name: isYeloAppSession() ? "yelo_app" : "jeeny",
      user_id: LocalStorage.getItem("user")?.user_id || "",
    };
    data.currency = {
      currency_short:
        classsPrice.price_details.currency_short ||
        classsPrice.price_details.currency_sign,
      currency_sign: getAggregatorDisplayCurrency(
        classsPrice.price_details,
        bookingFormPayload?.aggregator
      ),
    };

    if (bookingFormPayload?.aggregator) {
      data.aggregator = true;
      data.mc_quote = {
        external_class_id: classsPrice.external_class_id,
        external_class_name: classsPrice.external_class_name,
        external_class_image: classsPrice.external_class_image,
        quoted_price: classsPrice.price_details.brutto,
        external_pickup_type:
          classsPrice.external_pickup_type || classsPrice.talixo_pickup_type,
        vat_rate: classsPrice.vat_rate ?? classsPrice.price_details?.VAT,
        source:
          classsPrice.source ||
          bookingFormPayload.aggregator_source ||
          bookingFormPayload.mc_source,
        is_external_only: classsPrice.is_external_only,
        free_cancellation: Number(classsPrice.free_cancellation ?? 0),
        currency_short: classsPrice.price_details.currency_short,
        distance: bookingFormPayload.distance,
      };
      data.distance_in_km = bookingFormPayload.distance;
      stripAggregatorPromoFromBookingData(data);
    }

    // data.pickupSuggestion=pickupSuggestion
    // data.destinationSuggestion=destinationSuggestion



    delete data.date;
    delete data.time;
    delete data.phoneNumber;
    delete data.email;
    delete data.title;
    delete data.notes;
    delete data.country;
    delete data.last_name;
    delete data.first_name;
    delete data.pickup_sign;
    delete data.child_seats;
    delete data.country_code;

    /* No airport booking without a flight number. The passenger step asks
       for it, but this is the last point before the booking exists, and a
       booking that slipped past it (the field hidden after a reload, or a
       drop-off airport the step did not ask about) went through with the
       chauffeur unable to track the flight. Send the reader back to add it. */
    if (pickupIsAirport && !data.flight_number) {
      ToasterService.showError(
        t(
          "flight_number_required",
          "Add your flight number so your chauffeur can track your flight."
        )
      );
      setPage(2);
      navigate(stepPath(2), { replace: true });
      return;
    }

    submittingBooking.current = true;
    const [success]: any = await BookingService.addBooking(data);

    if (!success) {
      submittingBooking.current = false;
      return;
    }

    const responseData = success.data.data;

    if (shouldEnableYeloAppAnalytics()) {
      trackPartnerBookingCreated({
        trip_id: String(responseData.trip_id || ""),
        payment_gateway: responseData.payment_gateway,
        amount: responseData.price ?? classsPrice?.price_details?.brutto,
        currency:
          responseData.currency ||
          classsPrice?.price_details?.currency_short ||
          classsPrice?.price_details?.currency_sign,
      });
    }
    const {
      redirect_link,
      trip_id,
      client_secret,
      payment_receipt,
      stripe_client_secret,
      payment_gateway,
      price,
      currency,
      hyperpay,
      platform,
      hyperpayBillingGate,
    } = responseData;

    dispatch?.(modalActions.closeModal());

    // Jeeny partner flow — unchanged: redirect to Jeeny-hosted payment.
    if (!isYeloAppSession()) {
      if (redirect_link) {
        window.location.replace(redirect_link);
        return;
      }
      dispatch?.(bookingActions.setLoading(false));
      return;
    }

    const requiresPayment = classsPrice.price_details.brutto >= 1;

    if (stripe_client_secret && requiresPayment) {
      LocalStorage.setItem(PARTNER_CHECKOUT_STORAGE_KEY, {
        trip_id,
        price: price ?? classsPrice.price_details.brutto,
        currency:
          currency ||
          classsPrice.price_details.currency_short ||
          classsPrice.price_details.currency_sign,
        stripe_client_secret,
        client_secret: stripe_client_secret,
        payment_gateway: "Stripe",
      });
      navigate(withPartnerToken(`/payment-form?order=${trip_id}&gateway=stripe`));
      return;
    }

    if (
      requiresPayment &&
      (trip_id &&
        ((client_secret && payment_receipt) ||
          payment_gateway === "HyperPay" ||
          hyperpay ||
          responseData.form))
    ) {
      LocalStorage.setItem(PARTNER_CHECKOUT_STORAGE_KEY, {
        trip_id,
        price: price ?? classsPrice.price_details.brutto,
        currency:
          currency ||
          classsPrice.price_details.currency_short ||
          classsPrice.price_details.currency_sign,
        payfort_form: responseData.form || client_secret,
        client_secret: responseData.form || client_secret,
        form: responseData.form || client_secret,
        payment_gateway: payment_gateway || "HyperPay",
        platform: platform || "hyper-pay",
        hyperpay,
        hyperpayBillingGate,
      });
      navigate(withPartnerToken(`/payment-form?order=${trip_id}`));
      return;
    }

    if (trip_id && !requiresPayment) {
      navigate(withPartnerToken(`/booking/${trip_id}`));
      return;
    }

    submittingBooking.current = false;
    dispatch?.(bookingActions.setLoading(false));
    ToasterService.showError(
      String(t("paymentRequired", "Payment is required to complete your booking."))
    );
  };

  return (
    <>
      <FormHeader page={page} prevPage={prevPage} />
      <Container maxWidth="lg">
        {page === 1 ? (
          <div
            className="yp-flow header-padding"
            dir={direction}
          >
            <ServiceClassTabs onSubmit={nextPage} />
          </div>
        ) : null}
        {page === 2 && <AdditionalInfoForm onSubmit={nextPage} />}
        {page === reviewPage && classsPrice?.price_details && (
          <Checkout onSubmit={handleSubmit} onEditPassenger={prevPage} />
        )}
      </Container>
      <div id="payment_form_div"></div>
    </>
  );
}
